import { TopNav } from "@dashboard/components/AppLayout";
import { DashboardCard } from "@dashboard/components/Card";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Box, Input, Select, Text } from "@saleor/macaw-ui-next";
import { Search } from "lucide-react";
import React, { useMemo, useState } from "react";

import PageSection from "../PageSection/PageSection";

interface PageNode {
  id: string;
  title: string;
  slug: string;
  created: string;
  isPublished: boolean;
}

export interface PagesListPageProps {
  customerServicePages: PageNode[];
  legalPages: PageNode[];
  companyPages: PageNode[];
  onPageClick?: (id: string) => void;
}

export const PagesListPage: React.FC<PagesListPageProps> = ({
  customerServicePages,
  legalPages,
  companyPages,
  onPageClick,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [statusFilter, setStatusFilter] = useState("All Status");

  const filterPages = (pages: PageNode[]) => {
    let filtered = pages;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter(
        page => page.title.toLowerCase().includes(query) || page.slug.toLowerCase().includes(query),
      );
    }

    if (statusFilter !== "All Status") {
      filtered = filtered.filter(page =>
        statusFilter === "Published" ? page.isPublished : !page.isPublished,
      );
    }

    return filtered;
  };

  const filteredCustomerServicePages = useMemo(
    () =>
      categoryFilter === "All Categories" || categoryFilter === "Customer Service"
        ? filterPages(customerServicePages)
        : [],
    [customerServicePages, searchQuery, statusFilter, categoryFilter],
  );

  const filteredLegalPages = useMemo(
    () =>
      categoryFilter === "All Categories" || categoryFilter === "Legal"
        ? filterPages(legalPages)
        : [],
    [legalPages, searchQuery, statusFilter, categoryFilter],
  );

  const filteredCompanyPages = useMemo(
    () =>
      categoryFilter === "All Categories" || categoryFilter === "Company"
        ? filterPages(companyPages)
        : [],
    [companyPages, searchQuery, statusFilter, categoryFilter],
  );

  const hasAnyPages =
    filteredCustomerServicePages.length > 0 ||
    filteredLegalPages.length > 0 ||
    filteredCompanyPages.length > 0;

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <div style={{ padding: "24px 0px 0px 24px" }}>
        <TopNav
          title="Pages"
          subtitle="Manage your store's static pages using simple Markdown"
          display={"contents"}
        />
      </div>
      <DetailPageLayout.Content>
        <DashboardCard>
          <Box padding={6}>
            <Box display="flex" gap={3} alignItems="center" marginBottom={6}>
              <Box position="relative" style={{ flex: 1 }}>
                <Input
                  type="text"
                  placeholder="Search pages..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: 40 }}
                />
                <Box position="absolute" left={3} top={5} style={{ transform: "translateY(-50%)" }}>
                  <Search size={16} color="#616161" />
                </Box>
              </Box>

              <Select
                value={categoryFilter}
                onChange={value => setCategoryFilter(value as string)}
                options={[
                  { label: "All Categories", value: "All Categories" },
                  { label: "Customer Service", value: "Customer Service" },
                  { label: "Company", value: "Company" },
                  { label: "Legal", value: "Legal" },
                ]}
              />

              <Select
                value={statusFilter}
                onChange={value => setStatusFilter(value as string)}
                style={{ minWidth: 60 }}
                options={[
                  { label: "All Status", value: "All Status" },
                  { label: "Published", value: "Published" },
                  { label: "Draft", value: "Draft" },
                ]}
              />
            </Box>

            {!hasAnyPages ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                padding={12}
                style={{ minHeight: 300 }}
              >
                <Text size={4} color="default2" marginBottom={2}>
                  No pages found
                </Text>
                <Text size={2} color="default2">
                  Try adjusting your search or filter criteria
                </Text>
              </Box>
            ) : (
              <>
                {filteredCustomerServicePages.length > 0 && (
                  <PageSection
                    title="Customer Service"
                    description="Help and support pages"
                    pages={filteredCustomerServicePages}
                    onPageClick={onPageClick}
                  />
                )}

                {filteredCompanyPages.length > 0 && (
                  <PageSection
                    title="Company"
                    description="About your business"
                    pages={filteredCompanyPages}
                    onPageClick={onPageClick}
                  />
                )}

                {filteredLegalPages.length > 0 && (
                  <PageSection
                    title="Legal"
                    description="Terms, privacy, and policies"
                    pages={filteredLegalPages}
                    onPageClick={onPageClick}
                  />
                )}
              </>
            )}
          </Box>
        </DashboardCard>
      </DetailPageLayout.Content>
    </DetailPageLayout>
  );
};

PagesListPage.displayName = "PagesListPage";
export default PagesListPage;
